import React, { useState, useMemo } from 'react';
import { View, TouchableOpacity, StyleSheet } from 'react-native';
import { gql, useMutation, useQuery } from '@apollo/client';

import { useAuthState } from '../AuthState';
import { useSeason } from '../SeasonState';
import { LoadingIndicator, ApolloError, HStack } from '../../core-ui';
import {
  AmountInput,
  Button,
  Card,
  Checkbox,
  IconWithText,
  Page,
  Text,
  TextInput,
} from '../uiold';
import { themeColors } from '../uiold/Constants';
import { HiddenPrintView } from '../uiold/HiddenPrintView';
import { useNavigation, useParams } from '../Navigation';

import { showError, showSuccess } from '../App/AppAlert';
import { confirm } from '../App/AppDialog';

import { Billing, Payment } from '../../types/shared/Vendor';
import { getTaxPercent } from '../../support/shared/calculatePrice';
import VendorInvoiceSummary from '../Billing/VendorInvoiceSummary';
import { useShowApolloError } from '../../support/graphql/useShowApolloError';

import type {
  GetVendorInvoiceDetails,
  GetVendorInvoiceDetailsVariables,
} from './__generated__/GetVendorInvoiceDetails';
import type {
  DeleteInvoiceLineItems,
  DeleteInvoiceLineItemsVariables,
} from './__generated__/DeleteInvoiceLineItems';
import type {
  AddInvoiceAdjustment,
  AddInvoiceAdjustmentVariables,
} from './__generated__/AddInvoiceAdjustment';
import type {
  AddInvoicePayment,
  AddInvoicePaymentVariables,
} from './__generated__/AddInvoicePayment';
import type {
  SendVendorInvoiceEmail,
  SendVendorInvoiceEmailVariables,
} from './__generated__/SendVendorInvoiceEmail';
import { InvoiceMenu } from '../Billing/InvoiceMenu';

const GET_VENDOR_INVOICE_DETAILS = gql`
  query GetVendorInvoiceDetails($seasonId: String!, $vendorId: String!) {
    myAccount {
      id
      showNewFeatures
      settings {
        currency
      }
    }
    vendor(id: $vendorId) {
      id
      name
      contact
      billing
      payments
      priceTier {
        id
        tax_rate
        base_price_is_tax_exempt
      }
      createdAt
    }
    season(id: $seasonId) {
      id
      markets {
        id
        name
        dates
        numSpots
      }
      addons {
        id
        name
        defaultPrice
        createdAt
        updatedAt
      }
    }
  }
`;

const DELETE_INVOICE_LINE_ITEMS = gql`
  mutation DeleteInvoiceLineItems($vendorId: String!, $lineItems: [String!]!) {
    deleteInvoiceLineItems(vendorId: $vendorId, lineItems: $lineItems) {
      id
    }
  }
`;

const ADD_INVOICE_ADJUSTMENT = gql`
  mutation AddInvoiceAdjustment(
    $vendorId: String!
    $vendorInvoiceId: String!
    $adjustment: AdjustmentInput!
    $sendEmail: Boolean!
  ) {
    addInvoiceAdjustment(
      vendorId: $vendorId
      vendorInvoiceId: $vendorInvoiceId
      adjustment: $adjustment
      sendEmail: $sendEmail
    ) {
      vendor {
        id
      }
      sendError
    }
  }
`;

const ADD_INVOICE_PAYMENT = gql`
  mutation AddInvoicePayment(
    $vendorId: String!
    $invoiceId: String!
    $payment: AddInvoicePaymentInput!
    $sendEmail: Boolean!
  ) {
    addInvoicePayment(
      vendorId: $vendorId
      invoiceId: $invoiceId
      payment: $payment
      sendEmail: $sendEmail
    ) {
      vendor {
        id
      }
      sendError
    }
  }
`;

const SEND_VENDOR_INVOICE_EMAIL = gql`
  mutation SendVendorInvoiceEmail($vendorId: String!) {
    sendVendorInvoiceEmail(vendorId: $vendorId) {
      sendError
    }
  }
`;

function BillingSummary(props: { vendorId: string }) {
  const { vendorId } = props;
  const auth = useAuthState();
  const { season } = useSeason();

  const [adjustmentAmount, setAdjustmentAmount] = useState(0);
  const [adjustmentReason, setAdjustmentReason] = useState('');
  const [isAdjustmentTaxable, setIsAdjustmentTaxable] = useState(true);
  const [paymentAmount, setPaymentAmount] = useState(0);
  const [paymentReason, setPaymentReason] = useState('');
  const [vendorInvoiceId, setVendorInvoiceId] = useState<string | undefined>(
    undefined,
  );

  const showApolloError = useShowApolloError();

  const [deleteInvoiceLineItems] = useMutation<
    DeleteInvoiceLineItems,
    DeleteInvoiceLineItemsVariables
  >(DELETE_INVOICE_LINE_ITEMS, {
    onError: showApolloError,
    refetchQueries: [GET_VENDOR_INVOICE_DETAILS],
  });

  const [addInvoiceAdjustment] = useMutation<
    AddInvoiceAdjustment,
    AddInvoiceAdjustmentVariables
  >(ADD_INVOICE_ADJUSTMENT, {
    onError: showApolloError,
    refetchQueries: [GET_VENDOR_INVOICE_DETAILS],
  });

  const [addInvoicePayment] = useMutation<
    AddInvoicePayment,
    AddInvoicePaymentVariables
  >(ADD_INVOICE_PAYMENT, {
    onError: showApolloError,
    refetchQueries: [GET_VENDOR_INVOICE_DETAILS],
  });

  const [sendVendorInvoiceEmail] = useMutation<
    SendVendorInvoiceEmail,
    SendVendorInvoiceEmailVariables
  >(SEND_VENDOR_INVOICE_EMAIL, {
    onError: showApolloError,
  });

  const { data, loading, error } = useQuery<
    GetVendorInvoiceDetails,
    GetVendorInvoiceDetailsVariables
  >(GET_VENDOR_INVOICE_DETAILS, {
    variables: {
      seasonId: season?.id ?? '',
      vendorId,
    },
    fetchPolicy: 'cache-and-network',
  });

  const normalized = useMemo(() => {
    if (!data || !data.vendor || !data.season) {
      return null;
    }

    const {
      id,
      name,
      contact,
      priceTier,
      billing,
      payments,
      createdAt,
    } = data.vendor;
    const vendor = {
      id,
      name,
      contact,
      priceTier,
      billing: billing ? (JSON.parse(billing) as Billing) : undefined,
      payments: payments.map(
        (stringified) => JSON.parse(stringified) as Payment,
      ),
      created_at: createdAt,
    };

    const markets = data.season.markets.map((market) => {
      const { id, name, dates, numSpots } = market;
      return { id, name, dates, num_spots: numSpots };
    });

    const addons = new Map(
      data.season.addons.map((addon) => {
        const { id, name, defaultPrice, createdAt, updatedAt } = addon;
        const normalizedAddon = {
          id,
          name,
          defaultPrice,
          created_at: createdAt,
          modified_at: updatedAt,
        };
        return [id, normalizedAddon] as const;
      }),
    );

    return { vendor, markets, addons };
  }, [data]);

  if (error) {
    return <ApolloError error={error} />;
  }

  if (loading || !data) {
    return <LoadingIndicator />;
  }

  if (!normalized) {
    return <p>{t('Not found')}</p>;
  }

  const { vendor, markets, addons } = normalized;

  const billing = vendor.billing;
  const { settings, showNewFeatures } = data.myAccount;
  const currency = settings.currency ?? 'USD';
  const taxPercent = getTaxPercent(vendor.priceTier) / 100;

  const addAdjustment = async (vendorInvoiceId: string) => {
    if (
      adjustmentAmount === 0 ||
      isNaN(adjustmentAmount) ||
      adjustmentReason === ''
    ) {
      showError(t('Please enter an adjustment amount and reason'));
      return;
    }

    const shouldProceed = await confirm(
      t('Send Invoice?'),
      t('Press OK to create an adjustment and send an invoice to this vendor.'),
    );
    if (!shouldProceed) {
      return;
    }
    await addInvoiceAdjustment({
      variables: {
        vendorId,
        vendorInvoiceId,
        adjustment: {
          amount: adjustmentAmount,
          reason: adjustmentReason,
          taxable: isAdjustmentTaxable,
        },
        sendEmail: true,
      },
    });

    setAdjustmentAmount(0);
    setAdjustmentReason('');
    showSuccess(t('Invoice sent.'));
  };

  const addPayment = async (vendorInvoiceId: string) => {
    if (!vendorInvoiceId) {
      showError(t('Please choose an invoice to apply the payment to'));
      return;
    }
    if (paymentAmount === 0 || isNaN(paymentAmount) || paymentReason === '') {
      showError(t('Please enter a payment amount and reason'));
      return;
    }

    const shouldProceed = await confirm(
      t('Add Payment?'),
      t(
        'Press OK to add a payment. A confirmation email will be sent to this vendor.',
      ),
    );

    if (!shouldProceed) {
      return;
    }

    await addInvoicePayment({
      variables: {
        vendorId,
        invoiceId: vendorInvoiceId,
        payment: { amount: paymentAmount, reason: paymentReason },
        sendEmail: true,
      },
    });

    setPaymentAmount(0);
    setPaymentReason('');
    showSuccess(t('Payment created.'));
  };

  const sendEmail = async () => {
    const shouldProceed = await confirm(
      t('Send invoice email?'),
      t('Press OK to send an email with a link to the latest invoice.'),
    );
    if (!shouldProceed) {
      return;
    }
    await sendVendorInvoiceEmail({ variables: { vendorId: vendor.id } });
    showSuccess(t('Sent email to vendor: {name}.', { name: vendor.name }));
  };

  return (
    <>
      <Card.Header title={`${vendor.name} - ${vendor.contact}`} />
      {showNewFeatures && (
        <HStack pb={3}>
          <InvoiceMenu
            includeBillingSummary
            setVendorInvoiceId={(vendorInvoiceId) => {
              setVendorInvoiceId(vendorInvoiceId);
            }}
            vendorInvoiceId={vendorInvoiceId}
            vendorInvoices={vendor.billing?.invoices ?? []}
          />
        </HStack>
      )}
      {billing ? (
        <>
          <VendorInvoiceSummary
            vendor={vendor}
            markets={markets}
            currency={currency}
            addons={addons}
            allowSelection={auth.account.system_admin ?? false}
            renderSelectionToolbar={({ items, selectedItems, selectNone }) => {
              const count = selectedItems.size;
              return (
                <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                  <Text>
                    {t('{count} line item(s) selected', {
                      count,
                    })}
                  </Text>
                  <View style={{ flex: 1 }} />
                  <Button
                    isPrimary={true}
                    onPress={async () => {
                      const shouldProceed = await confirm(
                        t('Delete {count} line item(s)?', { count }),
                        t('This will delete {count} line item(s).', {
                          count,
                        }),
                      );
                      if (!shouldProceed) {
                        return;
                      }
                      // Note: LineItem does not currently have a unique ID
                      // (need a DB migration to fix this) so we're using the
                      // index as the lineItem ID, which is not ideal.
                      const toDelete: Array<string> = [];
                      items.forEach((item, index) => {
                        if (selectedItems.has(item)) {
                          toDelete.push(String(index));
                        }
                      });
                      await deleteInvoiceLineItems({
                        variables: { vendorId, lineItems: toDelete },
                      });
                      selectNone();
                    }}
                    title={t('Delete')}
                  />
                </View>
              );
            }}
            vendorInvoiceId={vendorInvoiceId}
          />
          <HiddenPrintView>
            <View style={styles.buttonGroup}>
              {vendorInvoiceId && (
                <>
                  <View style={styles.flexRow}>
                    <View style={styles.inputAmount}>
                      <Text style={styles.label}>{t('Adjustment Amount')}</Text>
                      <AmountInput
                        amount={adjustmentAmount}
                        onChange={(value) => {
                          setAdjustmentAmount(value);
                        }}
                      />
                    </View>
                    <View style={styles.adjustmentReason}>
                      <Text style={styles.label}>{t('Adjustment Reason')}</Text>
                      <TextInput
                        onChangeText={(value) => {
                          setAdjustmentReason(value);
                        }}
                        value={adjustmentReason}
                      />
                    </View>
                    <View style={styles.adjustmentTaxable}>
                      <Text style={styles.label}>{t('Taxable?')}</Text>
                      <Checkbox
                        isChecked={isAdjustmentTaxable}
                        onChange={() =>
                          setIsAdjustmentTaxable((isTaxable) => !isTaxable)
                        }
                      />
                    </View>
                    <View style={styles.adjustmentTaxable}>
                      <Text style={styles.label}>{t('Tax %')}</Text>
                      <Text>{`${Math.round(taxPercent * 100)}%`}</Text>
                    </View>
                  </View>
                  <Button
                    onPress={() => addAdjustment(vendorInvoiceId)}
                    title={t('Add Adjustment')}
                    isPrimary
                    style={styles.button}
                  />

                  <View style={styles.flexRow}>
                    <View style={styles.inputAmount}>
                      <Text style={styles.label}>{t('Payment Amount')}</Text>
                      <AmountInput
                        amount={paymentAmount}
                        onChange={(value) => {
                          if (value >= 0) {
                            setPaymentAmount(value);
                          }
                        }}
                      />
                    </View>
                    <View style={styles.inputReason}>
                      <Text style={styles.label}>{t('Payment Note')}</Text>
                      <TextInput
                        onChangeText={(value) => {
                          setPaymentReason(value);
                        }}
                        value={paymentReason}
                      />
                    </View>
                  </View>
                  <Button
                    onPress={() => addPayment(vendorInvoiceId)}
                    title={t('Add Payment')}
                    isPrimary
                    style={styles.button}
                  />
                </>
              )}

              <Button
                onPress={() => sendEmail()}
                title={t('Re-send Invoice email')}
                isPrimary
                style={styles.button}
              />
            </View>
          </HiddenPrintView>
        </>
      ) : (
        <Text>{t('Nothing has been invoiced yet for this vendor.')}</Text>
      )}
    </>
  );
}

export default function VendorPaymentDetailsPage() {
  const { backTo } = useNavigation();
  const params = useParams();
  const vendorId = params.id ?? '';
  return (
    <Page>
      <HiddenPrintView>
        <Page.Header
          title={t('Vendor Billing Details')}
          showSeparator={false}
        />
        <TouchableOpacity
          onPress={() => backTo('/billing/vendors')}
          style={{ marginBottom: 8 }}
        >
          <IconWithText
            iconName="arrowBack"
            text={t('Back to list')}
            color={themeColors.mainNavText}
          />
        </TouchableOpacity>
      </HiddenPrintView>
      <Card>
        <BillingSummary vendorId={vendorId} />
      </Card>
    </Page>
  );
}

const styles = StyleSheet.create({
  adjustmentReason: {
    marginRight: 20,
    width: '40%',
  },
  adjustmentTaxable: {
    marginRight: 20,
    width: '10%',
  },
  buttonGroup: {
    marginBottom: 100,
    marginTop: 30,
  },
  label: { marginVertical: 4 },
  inputReason: {
    width: '60%',
    marginRight: 20,
  },
  inputAmount: {
    width: '40%',
    paddingRight: 20,
  },
  flexRow: { flexDirection: 'row' },
  button: {
    width: 'max-content',
    marginTop: 8,
    marginBottom: 24,
  },
});
